<script setup>
import { computed } from 'vue'
import { useLocaleStore } from '@/stores/localeStore'
import skillsData from '@/data/skills-data.json'
import NextButton from '../NextButton.vue'
import PreviousButton from '../PreviousButton.vue'

defineEmits(['open-window', 'minimize-window'])

const localeStore = useLocaleStore()
const currentLocale = computed(() => localeStore.currentLocale)

const hardSkillsData = computed(() => {
  return skillsData[currentLocale.value]?.hardSkills || []
})

const softSkillsData = computed(() => {
  return skillsData[currentLocale.value]?.softSkills || []
})

const autoEvaluationData = computed(() => {
  return skillsData[currentLocale.value]?.autoEvaluation || null
})
</script>

<template>
  <div class="relative right-0 h-content-window overflow-hidden">
    <div class="w-full h-full bg-gradient-to-br from-gray-50 to-white overflow-auto pb-28">
      <div class="max-w-6xl mx-auto p-6 md:p-8">
        <!-- Header -->
        <div class="bg-white rounded-2xl p-6 md:p-8 shadow-xl border border-gray-100 mb-8">
          <h2 class="font-trebuchet-pixel text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            {{ $t('windows.competences.title') }}
          </h2>
          <hr class="border-t border-gray-300 mb-4" />
          <p class="text-sm md:text-base text-gray-700 leading-relaxed">
            {{ $t('windows.competences.description') }}
          </p>
        </div>

        <!-- Hard Skills -->
        <div class="bg-white rounded-2xl p-6 md:p-8 shadow-xl border border-gray-100 mb-6">
          <h2 class="font-trebuchet-pixel text-xl md:text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <div class="p-2 bg-blue-100 rounded-lg">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="text-blue-600">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
              </svg>
            </div>
            {{ $t('windows.competences.hardSkills') }}
          </h2>
          <hr class="border-t border-gray-200 mb-4" />

          <div class="space-y-6">
            <div
              v-for="skill in hardSkillsData"
              :key="skill.id"
              class="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-5 border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all duration-200"
            >
              <h3 class="font-trebuchet-pixel font-semibold text-base mb-2 text-gray-900">
                {{ skill.id }}. {{ skill.title }}
              </h3>
              <p class="text-sm text-gray-700 mb-3 leading-relaxed">{{ skill.description }}</p>
              
              <div v-if="skill.example" class="mt-3 pt-3 border-t border-gray-200">
                <p class="text-xs text-gray-700 italic">{{ skill.example }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Soft Skills -->
        <div class="bg-white rounded-2xl p-6 md:p-8 shadow-xl border border-gray-100 mb-6">
          <h2 class="font-trebuchet-pixel text-xl md:text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <div class="p-2 bg-green-100 rounded-lg">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                class="text-green-600"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            {{ $t('windows.competences.softSkills') }}
          </h2>
          <hr class="border-t border-gray-200 mb-4" />
          <div class="space-y-6">
            <div
              v-for="skill in softSkillsData"
              :key="skill.id"
              class="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-5 border border-green-200 hover:border-green-300 hover:shadow-md transition-all duration-200"
            >
              <h3 class="font-trebuchet-pixel font-semibold text-base mb-2 text-gray-900">
                {{ skill.id }}. {{ skill.title }}
              </h3>
              <p class="text-sm text-gray-700 mb-3 leading-relaxed">{{ skill.description }}</p>
              
              <div v-if="skill.example" class="mt-3 pt-3 border-t border-gray-200">
                <p class="text-xs text-gray-700 italic">{{ skill.example }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Auto-évaluation -->
        <div v-if="autoEvaluationData" class="bg-white rounded-2xl p-6 md:p-8 shadow-xl border border-gray-100 mb-6">
          <h2 class="font-trebuchet-pixel text-xl md:text-2xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <div class="p-2 bg-purple-100 rounded-lg">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="text-purple-600">
                <path d="M9 11l3 3L22 4" />
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
              </svg>
            </div>
            {{ autoEvaluationData.title }}
          </h2>
          <hr class="border-t border-gray-200 mb-4" />

          <!-- Acquis -->
          <div v-if="autoEvaluationData.acquis" class="mb-6">
            <h3 class="font-trebuchet-pixel font-semibold text-base mb-3 text-gray-900">{{ autoEvaluationData.acquis.title }}</h3>
            <div class="space-y-3">
              <div
                v-for="(acquis, index) in autoEvaluationData.acquis.items"
                :key="index"
                class="bg-gradient-to-br from-green-50 to-white rounded-lg p-4 border border-green-200"
              >
                <div class="flex items-start justify-between mb-2">
                  <h4 class="font-trebuchet-pixel font-semibold text-sm text-gray-900">{{ acquis.competence }}</h4>
                  <span class="text-xxs bg-green-100 text-green-700 px-2 py-1 rounded font-semibold">{{ acquis.niveau }}</span>
                </div>
                <p class="text-xs text-gray-700 italic">{{ acquis.preuve }}</p>
              </div>
            </div>
          </div>

          <!-- Lacunes -->
          <div v-if="autoEvaluationData.lacunes" class="mb-6">
            <h3 class="font-trebuchet-pixel font-semibold text-base mb-3 text-gray-900">{{ autoEvaluationData.lacunes.title }}</h3>
            <div class="space-y-3">
              <div
                v-for="(lacune, index) in autoEvaluationData.lacunes.items"
                :key="index"
                class="bg-gradient-to-br from-orange-50 to-white rounded-lg p-4 border border-orange-200"
              >
                <h4 class="font-trebuchet-pixel font-semibold text-sm text-gray-900 mb-1">{{ lacune.lacune }}</h4>
                <p class="text-xs text-gray-700 mb-2">{{ lacune.besoin }}</p>
                <p class="text-xs text-gray-600 italic">
                  <span class="font-semibold">Action :</span> {{ lacune.action }}
                </p>
              </div>
            </div>
          </div>

          <!-- Besoins -->
          <div v-if="autoEvaluationData.besoins">
            <h3 class="font-trebuchet-pixel font-semibold text-base mb-3 text-gray-900">{{ autoEvaluationData.besoins.title }}</h3>
            <ul class="list-disc list-inside text-sm text-gray-700 space-y-2 ml-2">
              <li v-for="(besoin, index) in autoEvaluationData.besoins.items" :key="index">{{ besoin }}</li>
            </ul>
          </div>
        </div>

        <!-- Note RNCP -->
        <div class="bg-blue-50 rounded-xl p-4 md:p-6 border border-blue-200">
          <div class="flex items-start gap-3">
            <div class="p-2 bg-blue-100 rounded-lg flex-shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="text-blue-600">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
              </svg>
            </div>
            <div class="flex-1">
              <h3 class="font-trebuchet-pixel font-semibold text-sm text-gray-900 mb-2">{{ $t('windows.competences.rncpNoteTitle') }}</h3>
              <p class="text-xs text-gray-700 leading-relaxed">
                {{ skillsData[currentLocale]?.rncpNote }}
              </p>
            </div>
          </div>
        </div>
      </div>
      <PreviousButton current-window-id="competences" @open-window="$emit('open-window', $event)" @minimize-window="$emit('minimize-window', $event)" />
      <NextButton current-window-id="competences" @open-window="$emit('open-window', $event)" @minimize-window="$emit('minimize-window', $event)" />
    </div>
  </div>
</template>
